/**
 * Debounced Processing Utilities
 * 
 * Provides efficient debouncing and throttling mechanisms for high-frequency
 * event processing. Prevents excessive function calls during rapid user input
 * while maintaining responsive user experience.
 */

/**
 * Configuration options for debouncing
 */
export interface DebounceOptions {
  /** Delay in milliseconds before execution */
  delay: number;
  /** Whether to execute on the leading edge (immediate first call) */
  leading?: boolean;
  /** Whether to execute on the trailing edge (after delay) */
  trailing?: boolean;
  /** Maximum wait time before forced execution */
  maxWait?: number;
}

/**
 * Configuration options for throttling
 */
export interface ThrottleOptions {
  /** Minimum time between executions in milliseconds */
  interval: number;
  /** Whether to execute on the leading edge */
  leading?: boolean;
  /** Whether to execute on the trailing edge */
  trailing?: boolean;
}

/**
 * Debounced function wrapper
 */
export interface DebouncedFunction<T extends (...args: any[]) => any> {
  (...args: Parameters<T>): void;
  cancel(): void;
  flush(): ReturnType<T> | undefined;
  pending(): boolean;
}

/**
 * Throttled function wrapper
 */
export interface ThrottledFunction<T extends (...args: any[]) => any> {
  (...args: Parameters<T>): ReturnType<T> | undefined;
  cancel(): void;
  flush(): ReturnType<T> | undefined;
}

/**
 * Creates a debounced version of a function that delays execution until
 * after the specified delay has elapsed since the last invocation
 * 
 * @param func The function to debounce
 * @param options Debounce configuration options
 * @returns Debounced function with control methods
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  options: DebounceOptions
): DebouncedFunction<T> {
  const { delay, leading = false, trailing = true, maxWait } = options;
  
  let timeoutId: number | undefined;
  let maxTimeoutId: number | undefined;
  let lastCallTime: number | undefined;
  let lastInvokeTime = 0;
  let lastArgs: Parameters<T> | undefined;
  let lastThis: any;
  let result: ReturnType<T> | undefined;

  function invokeFunc(time: number): ReturnType<T> {
    const args = lastArgs!;
    const thisBinding = lastThis;

    lastArgs = undefined;
    lastThis = undefined;
    lastInvokeTime = time;
    result = func.apply(thisBinding, args);
    return result!
  }

  function leadingEdge(time: number): ReturnType<T> {
    // Reset any existing timeout
    lastInvokeTime = time;
    // Start the timer for the trailing edge
    timeoutId = window.setTimeout(timerExpired, delay);
    // Invoke the leading edge
    return leading ? invokeFunc(time) : result!;
  }

  function remainingWait(time: number): number {
    const timeSinceLastCall = time - lastCallTime!;
    const timeSinceLastInvoke = time - lastInvokeTime;
    const timeWaiting = delay - timeSinceLastCall;

    return maxWait !== undefined
      ? Math.min(timeWaiting, maxWait - timeSinceLastInvoke)
      : timeWaiting;
  }

  function shouldInvoke(time: number): boolean {
    const timeSinceLastCall = time - (lastCallTime || 0);
    const timeSinceLastInvoke = time - lastInvokeTime;

    // Either this is the first call, activity has stopped and we're at the
    // trailing edge, the system time has gone backwards and we're treating
    // it as the trailing edge, or we've hit the `maxWait` limit.
    return (
      lastCallTime === undefined ||
      timeSinceLastCall >= delay ||
      timeSinceLastCall < 0 ||
      (maxWait !== undefined && timeSinceLastInvoke >= maxWait)
    );
  }

  function timerExpired(): ReturnType<T> | undefined {
    const time = Date.now();
    if (shouldInvoke(time)) {
      return trailingEdge(time);
    }
    // Restart the timer
    timeoutId = window.setTimeout(timerExpired, remainingWait(time));
    return undefined;
  }

  function trailingEdge(time: number): ReturnType<T> | undefined {
    timeoutId = undefined;

    // Only invoke if we have `lastArgs` which means `func` has been
    // debounced at least once.
    if (trailing && lastArgs) {
      return invokeFunc(time);
    }
    lastArgs = undefined;
    lastThis = undefined;
    return result;
  }

  function cancel(): void {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
    if (maxTimeoutId !== undefined) {
      clearTimeout(maxTimeoutId);
    }
    lastInvokeTime = 0;
    lastArgs = undefined;
    lastCallTime = undefined;
    lastThis = undefined;
    timeoutId = undefined;
    maxTimeoutId = undefined;
  }

  function flush(): ReturnType<T> | undefined {
    return timeoutId === undefined ? result : trailingEdge(Date.now());
  }

  function pending(): boolean {
    return timeoutId !== undefined;
  }

  function debounced(this: any, ...args: Parameters<T>): void {
    const time = Date.now();
    const isInvoking = shouldInvoke(time);

    lastArgs = args;
    lastThis = this;
    lastCallTime = time;

    if (isInvoking) {
      if (timeoutId === undefined) {
        leadingEdge(lastCallTime);
      } else {
        // Handle maxWait timeout
        if (maxWait !== undefined) {
          maxTimeoutId = window.setTimeout(() => {
            invokeFunc(Date.now());
          }, maxWait);
        }
      }
    }

    if (timeoutId === undefined) {
      timeoutId = window.setTimeout(timerExpired, delay);
    }
  }

  debounced.cancel = cancel;
  debounced.flush = flush;
  debounced.pending = pending;

  return debounced as DebouncedFunction<T>;
}

/**
 * Creates a throttled version of a function that only executes at most
 * once per specified interval
 * 
 * @param func The function to throttle
 * @param options Throttle configuration options
 * @returns Throttled function with control methods
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  options: ThrottleOptions
): ThrottledFunction<T> {
  const { interval, leading = true, trailing = true } = options;
  
  return debounce(func, {
    delay: interval,
    leading,
    trailing,
    maxWait: interval
  }) as ThrottledFunction<T>;
}

/**
 * Predefined debounce configurations for common use cases
 */
export const DebouncePresets = {
  /** Fast input processing - 100ms delay */
  FAST_INPUT: { delay: 100, leading: false, trailing: true },
  
  /** Standard input processing - 250ms delay */
  STANDARD_INPUT: { delay: 250, leading: false, trailing: true },
  
  /** Slow input processing - 500ms delay */
  SLOW_INPUT: { delay: 500, leading: false, trailing: true },
  
  /** Search input - 300ms delay with max 1000ms wait */
  SEARCH: { delay: 300, leading: false, trailing: true, maxWait: 1000 },
  
  /** Validation - immediate first, then 500ms delay (PRD requirement) */
  VALIDATION: { delay: 500, leading: true, trailing: true },
  
  /** Resize/scroll events - 100ms throttle */
  RESIZE_SCROLL: { delay: 100, leading: true, trailing: false },
  
  /** Button click protection - 1000ms delay */
  BUTTON_PROTECTION: { delay: 1000, leading: true, trailing: false }
} as const;

/**
 * Predefined throttle configurations for common use cases
 */
export const ThrottlePresets = {
  /** Fast UI updates - 16ms (60fps) */
  ANIMATION: { interval: 16, leading: true, trailing: false },
  
  /** Standard UI updates - 100ms */
  STANDARD: { interval: 100, leading: true, trailing: true },
  
  /** Network requests - 500ms */
  NETWORK: { interval: 500, leading: true, trailing: true },
  
  /** Scroll/resize events - 100ms */
  SCROLL: { interval: 100, leading: false, trailing: true }
} as const;

/**
 * Creates a debounced function specifically optimized for input events
 * Uses the standard input preset with customization options
 * 
 * @param func The function to debounce
 * @param customOptions Optional customization of standard input preset
 * @returns Debounced input function
 */
export function debounceInput<T extends (...args: any[]) => any>(
  func: T,
  customOptions?: Partial<DebounceOptions>
): DebouncedFunction<T> {
  const options = { ...DebouncePresets.STANDARD_INPUT, ...customOptions };
  return debounce(func, options);
}

/**
 * Creates a debounced function specifically optimized for validation events
 * Uses the validation preset with customization options
 * 
 * @param func The function to debounce
 * @param customOptions Optional customization of validation preset
 * @returns Debounced validation function
 */
export function debounceValidation<T extends (...args: any[]) => any>(
  func: T,
  customOptions?: Partial<DebounceOptions>
): DebouncedFunction<T> {
  const options = { ...DebouncePresets.VALIDATION, ...customOptions };
  return debounce(func, options);
}

/**
 * Creates a throttled function specifically optimized for scroll/resize events
 * Uses the scroll preset with customization options
 * 
 * @param func The function to throttle
 * @param customOptions Optional customization of scroll preset
 * @returns Throttled scroll function
 */
export function throttleScroll<T extends (...args: any[]) => any>(
  func: T,
  customOptions?: Partial<ThrottleOptions>
): ThrottledFunction<T> {
  const options = { ...ThrottlePresets.SCROLL, ...customOptions };
  return throttle(func, options);
}

/**
 * Request Animation Frame based throttling for smooth animations
 * Ensures function executes at most once per frame
 * 
 * @param func The function to throttle
 * @returns RAF-throttled function
 */
export function throttleRAF<T extends (...args: any[]) => any>(
  func: T
): ((...args: Parameters<T>) => void) & { cancel: () => void } {
  let rafId: number | null = null;
  let lastArgs: Parameters<T> | null = null;

  const throttled = function(this: any, ...args: Parameters<T>) {
    lastArgs = args;
    const thisArg = this;
    
    if (rafId === null) {
      rafId = requestAnimationFrame(() => {
        if (lastArgs) {
          func.apply(thisArg, lastArgs);
          lastArgs = null;
        }
        rafId = null;
      });
    }
  };

  throttled.cancel = () => {
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
      lastArgs = null;
    }
  };

  return throttled;
}

/**
 * Batch processor for collecting and processing multiple events together
 * Useful for API calls or expensive operations that can be batched
 */
export class BatchProcessor<T> {
  private batch: T[] = [];
  private timeoutId: number | undefined;
  private readonly maxBatchSize: number;
  private readonly flushDelay: number;
  private readonly processor: (batch: T[]) => void;

  constructor(
    processor: (batch: T[]) => void,
    options: {
      maxBatchSize?: number;
      flushDelay?: number;
    } = {}
  ) {
    this.processor = processor;
    this.maxBatchSize = options.maxBatchSize || 10;
    this.flushDelay = options.flushDelay || 100;
  }

  /**
   * Add an item to the batch
   */
  add(item: T): void {
    this.batch.push(item);

    // Flush if batch is full
    if (this.batch.length >= this.maxBatchSize) {
      this.flush();
      return;
    }

    // Schedule flush if not already scheduled
    if (this.timeoutId === undefined) {
      this.timeoutId = window.setTimeout(() => {
        this.flush();
      }, this.flushDelay);
    }
  }

  /**
   * Force flush the current batch
   */
  flush(): void {
    if (this.timeoutId !== undefined) {
      clearTimeout(this.timeoutId);
      this.timeoutId = undefined;
    }

    if (this.batch.length > 0) {
      const batchToProcess = [...this.batch];
      this.batch = [];
      this.processor(batchToProcess);
    }
  }

  /**
   * Cancel pending flush and clear batch
   */
  cancel(): void {
    if (this.timeoutId !== undefined) {
      clearTimeout(this.timeoutId);
      this.timeoutId = undefined;
    }
    this.batch = [];
  }

  /**
   * Get current batch size
   */
  size(): number {
    return this.batch.length;
  }

  /**
   * Check if batch is empty
   */
  isEmpty(): boolean {
    return this.batch.length === 0;
  }
}